import type { CellValueChangedEvent, GridApi, GridReadyEvent } from 'ag-grid-enterprise';
import { useCallback, useState } from 'react';
import type { ProjectLogEntry } from '../../services/timeEntryService';
import { generateId } from '../../utils/generateId';

interface UseGridHandlersProps {
  entries: ProjectLogEntry[];
  onChange: (entries: ProjectLogEntry[]) => void;
}

export const useGridHandlers = ({ entries, onChange }: UseGridHandlersProps) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const addNewRow = useCallback(() => {
    const newEntry: ProjectLogEntry = {
      id: generateId(),
      project_info: {
        project_id: '',
        project_number: '',
        project_name: '',
      },
      order_info: {
        order_id: '',
        order_number: '',
        comments: '',
      },
      task_info: {
        task_id: '',
        task_code: '',
        task_name: '',
        billable: false,
      },
      hours_logged: 0,
      description: '',
      billable: false,
    };
    onChange([...entries, newEntry]);
  }, [entries, onChange]);

  const deleteRow = useCallback(
    (id: string) => {
      onChange(entries.filter((entry) => entry.id !== id));
    },
    [entries, onChange]
  );

  const editRow = useCallback(
    (entry: ProjectLogEntry) => {
      const updatedEntries = entries.map((e) => (e.id === entry.id ? entry : e));
      onChange(updatedEntries);
    },
    [entries, onChange]
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      const { data, colDef, newValue } = event;
      const updatedEntries = entries.map((entry) => {
        if (entry.id === data.id) {
          return { ...entry, [colDef.field!]: newValue };
        }
        return entry;
      });
      onChange(updatedEntries);
    },
    [entries, onChange]
  );

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleExportExcel = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsExcel({
        fileName: 'project-logged-hours.xlsx',
        sheetName: 'Project Hours',
      });
    }
  }, [gridApi]);

  const handleExportCsv = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'project-logged-hours.csv',
      });
    }
  }, [gridApi]);

  return {
    gridApi,
    isLoading,
    setIsLoading,
    addNewRow,
    deleteRow,
    editRow,
    handleCellValueChanged,
    handleGridReady,
    handleFirstDataRendered,
    handleRowDataUpdated,
    handleExportExcel,
    handleExportCsv,
  };
};
